import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  useRxCollectionEpocusExam,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import type {
  AortaWorksheetData,
  BladderWorksheetData,
  CardiacWorksheetData,
  EFastWorksheetData,
  ExamWorksheetData,
  LungWorksheetData,
  PregnancyWorksheetData,
  RenalWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { useRxCollectionEpocusReview } from "$global-providers/rxdb-provider/collections/epocus-review";
import { useUser } from "$global-providers/user-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { formatDicomPatientName } from "../../_lib/dicom-patient-name";
import { EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import { IDocumentsAccepted, IDocumentsDenied } from "../../_lib/epocus-icons";
import { EpocusImageGallery } from "../../_lib/epocus-image-gallery";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_PRIMARY_DARK,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { EpocusStarRating } from "../../_lib/epocus-star-rating";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { useEpocusExamImages } from "../../_lib/hooks/use-epocus-exam-images";
import { RoleGate } from "../../_lib/rbac/role-gate";
import { linkToEpocusReview } from "../../links";
import { AiFindingsPanel } from "./_lib/ai-findings-panel";

// ─── Feedback chip options ────────────────────────────────────────────────────

const FEEDBACK_CHIPS = [
  "Great Job",
  "Too Deep",
  "Too Shallow",
  "Too Dark",
  "Too Bright",
  "Wrong Orientation",
  "Insufficient Views",
  "Pathology Missed",
  "Overcalled",
] as const;

// ─── Worksheet helpers ────────────────────────────────────────────────────────

const VIEW_LABELS: Record<string, string> = {
  ruq: "RUQ",
  luq: "LUQ",
  suprapubic: "Suprapubic",
  subxiphoid: "Subxiphoid",
  rLung: "R Lung",
  lLung: "L Lung",
  plax: "PLAX",
  psax: "PSAX",
  apical4Chamber: "A4C",
  rUpperAnterior: "R Upper Ant",
  rLowerAnterior: "R Lower Ant",
  rPosterior: "R Post",
  lUpperAnterior: "L Upper Ant",
  lLowerAnterior: "L Lower Ant",
  lPosterior: "L Post",
  sagittal: "Sagittal",
  transverse: "Transverse",
  rKidneyLong: "R Long",
  rKidneyTrans: "R Trans",
  lKidneyLong: "L Long",
  lKidneyTrans: "L Trans",
  bladder: "Bladder",
  proximalTrans: "Prox Trans",
  proximalSag: "Prox Sag",
  midTrans: "Mid Trans",
  midSag: "Mid Sag",
  distalTrans: "Dist Trans",
  distalSag: "Dist Sag",
  transabdominalSagittal: "TA Sag",
  transabdominalTransverse: "TA Trans",
  transvaginalSagittal: "TV Sag",
  transvaginalTransverse: "TV Trans",
};

const formatIndication = (indication: string | null): string => {
  if (!indication) return "";
  return indication.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getCompletedViews = (views: Record<string, boolean>): string[] =>
  Object.entries(views)
    .filter(([, v]) => v)
    .map(([k]) => VIEW_LABELS[k] ?? k);

const summarizeEFast = (data: EFastWorksheetData): string => {
  const positive = Object.entries(data.findings)
    .filter(([, v]) => v === "present")
    .map(([k]) =>
      k
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()
        .trim(),
    );
  return positive.length > 0 ? positive.join(", ") : "No positive findings";
};

const summarizeCardiac = (data: CardiacWorksheetData): string => {
  const items: string[] = [];
  if (data.findings.lvFunction)
    items.push(`LV: ${data.findings.lvFunction.replace(/_/g, " ")}`);
  if (
    data.findings.pericardialEffusion &&
    data.findings.pericardialEffusion !== "none"
  )
    items.push(`Pericardial effusion: ${data.findings.pericardialEffusion}`);
  if (data.findings.rvDilatation) items.push("RV dilatation");
  return items.length > 0 ? items.join(", ") : "No significant findings";
};

const summarizeLung = (data: LungWorksheetData): string => {
  const abnormal: string[] = [];
  for (const [zone, findings] of Object.entries(data.zones)) {
    if (findings.lungSliding === false) abnormal.push(`${zone}: no sliding`);
    if (findings.bLines === "3+" || findings.bLines === "confluent")
      abnormal.push(`${zone}: B-lines`);
    if (findings.pleuralEffusion && findings.pleuralEffusion !== "none")
      abnormal.push(`${zone}: effusion`);
  }
  return abnormal.length > 0 ? abnormal.join(", ") : "Normal lung findings";
};

const summarizeBladder = (data: BladderWorksheetData): string => {
  const items: string[] = [];
  if (data.findings.bladderVolumeMl !== null)
    items.push(`Vol: ${data.findings.bladderVolumeMl}mL`);
  if (data.findings.distension && data.findings.distension !== "normal")
    items.push(`Distension: ${data.findings.distension}`);
  return items.length > 0 ? items.join(", ") : "Normal bladder";
};

const summarizeRenal = (data: RenalWorksheetData): string => {
  const items: string[] = [];
  for (const [label, findings] of [
    ["R kidney", data.findings.rKidney],
    ["L kidney", data.findings.lKidney],
  ] as const) {
    if (findings.hydronephrosis && findings.hydronephrosis !== "none")
      items.push(`${label}: hydronephrosis (${findings.hydronephrosis})`);
    if (findings.stones) items.push(`${label}: stones`);
  }
  return items.length > 0 ? items.join(", ") : "No renal abnormalities";
};

const summarizeAorta = (data: AortaWorksheetData): string => {
  const items: string[] = [];
  for (const [label, findings] of [
    ["Proximal", data.findings.proximal],
    ["Mid", data.findings.mid],
    ["Distal", data.findings.distal],
  ] as const) {
    if (findings.aneurysm) items.push(`${label}: aneurysm`);
    else if (findings.diameterCm !== null && findings.diameterCm > 3)
      items.push(`${label}: ${findings.diameterCm}cm`);
  }
  return items.length > 0 ? items.join(", ") : "Normal aorta";
};

const summarizePregnancy = (data: PregnancyWorksheetData): string => {
  const items: string[] = [];
  const f = data.findings;
  if (f.intrauterineGestation === false) items.push("No IUP visualised");
  else if (f.intrauterineGestation) items.push("IUP visualised");
  if (f.cardiacActivity === "absent") items.push("no cardiac activity");
  else if (f.cardiacActivity === "present" && f.fetalHeartRateBpm !== null)
    items.push(`FHR ${f.fetalHeartRateBpm} bpm`);
  if (f.numberOfGestations !== null && f.numberOfGestations > 1)
    items.push(`${f.numberOfGestations} gestations`);
  if (f.estimatedGestationalAgeWeeks !== null)
    items.push(`EGA ${f.estimatedGestationalAgeWeeks} wk`);
  if (f.estimatedDueDate) {
    // EDD is a date-only string. Parse as UTC midnight AND format with
    // timeZone: "UTC" so viewers in negative offsets don't see the previous
    // calendar day.
    const edd = new Date(`${f.estimatedDueDate}T00:00:00Z`);
    const formatted = Number.isNaN(edd.getTime())
      ? f.estimatedDueDate
      : edd.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        });
    items.push(`EDD ${formatted}`);
  }
  if (f.estimatedFetalWeightG !== null)
    items.push(`EFW ${f.estimatedFetalWeightG} g`);
  if (f.fetalPresentation && f.fetalPresentation !== "cephalic")
    items.push(`${f.fetalPresentation} presentation`);
  if (f.uterineFibroidsSeen) items.push("fibroids");
  if (f.pelvicFreeFluid && f.pelvicFreeFluid !== "none")
    items.push(`pelvic free fluid: ${f.pelvicFreeFluid}`);
  return items.length > 0 ? items.join(", ") : "No significant findings";
};

const summarizeWorksheet = (data: ExamWorksheetData): string => {
  switch (data.examType) {
    case "efast":
      return summarizeEFast(data);
    case "cardiac":
      return summarizeCardiac(data);
    case "lung":
      return summarizeLung(data);
    case "bladder":
      return summarizeBladder(data);
    case "renal":
      return summarizeRenal(data);
    case "aorta":
      return summarizeAorta(data);
    case "pregnancy":
      return summarizePregnancy(data);
    default:
      return "No summary available";
  }
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  backgroundColor: EPOCUS_BG,
});

const Header = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: "14px 24px",
  },
});

const BackButton = styled("button")({
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  cursor: "pointer",
  borderRadius: 10,
  color: EPOCUS_PRIMARY,
  flexShrink: 0,
  "&:hover": { backgroundColor: EPOCUS_BG },
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
});

const HeaderTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const HeaderSubtitle = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// Two-column on tablet, single column on phone
const ContentLayout = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 0,
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 0,
  },
});

const LeftColumn = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  [epocusTheme.breakpoints.up("sm")]: {
    flex: "0 0 50%",
    maxWidth: "50%",
    padding: 24,
    borderRight: `1px solid ${EPOCUS_BORDER}`,
    position: "sticky",
    top: 0,
    maxHeight: "calc(100dvh - 72px)",
    overflowY: "auto",
  },
});

const RightColumn = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  [epocusTheme.breakpoints.up("sm")]: {
    flex: 1,
    padding: 24,
  },
});

const Card = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  border: `1px solid ${EPOCUS_BORDER}`,
  padding: 16,
});

const SectionLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: EPOCUS_TEXT_SECONDARY,
  textTransform: "uppercase",
  marginBottom: 10,
});

const FindingsText = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_PRIMARY,
  lineHeight: 1.5,
});

const ViewChips = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginTop: 8,
});

const ViewBadge = styled(Box)({
  backgroundColor: EPOCUS_BG,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 20,
  padding: "3px 10px",
  fontSize: rem(12),
  color: EPOCUS_TEXT_PRIMARY,
});

// Agree / Disagree button — selection state applied via inline style
const VerdictButton = styled(Button)({
  flex: 1,
  minHeight: 52,
  borderRadius: 12,
  fontSize: rem(15),
  fontWeight: 700,
  textTransform: "none",
});

const FeedbackChipRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

const SubmitButton = styled(Button)({
  minHeight: 52,
  borderRadius: 12,
  fontSize: rem(16),
  fontWeight: 700,
  textTransform: "none",
  width: "100%",
  backgroundColor: EPOCUS_PRIMARY,
  color: EPOCUS_WHITE,
  "&:hover": {
    backgroundColor: EPOCUS_PRIMARY_DARK,
  },
  "&:disabled": {
    backgroundColor: EPOCUS_BORDER,
    color: EPOCUS_TEXT_SECONDARY,
  },
});

const SuccessBanner = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: "48px 24px",
  textAlign: "center",
  flex: 1,
});

// ─── Worksheet Card ───────────────────────────────────────────────────────────

const WorksheetCard: FC<{ exam: EpocusExam }> = ({ exam }) => {
  const { worksheetData } = exam;
  const indication = formatIndication(worksheetData.indication ?? null);
  const completedViews = getCompletedViews(
    worksheetData.views as Record<string, boolean>,
  );
  const keyFindings = summarizeWorksheet(worksheetData);
  const notes = worksheetData.notes;

  return (
    <Card>
      <SectionLabel>Worksheet Summary</SectionLabel>
      {indication && (
        <Box mb={1}>
          <Typography
            style={{
              fontSize: rem(11),
              color: EPOCUS_TEXT_SECONDARY,
              fontWeight: 600,
            }}
          >
            INDICATION
          </Typography>
          <FindingsText>{indication}</FindingsText>
        </Box>
      )}
      {completedViews.length > 0 && (
        <Box mb={1}>
          <Typography
            style={{
              fontSize: rem(11),
              color: EPOCUS_TEXT_SECONDARY,
              fontWeight: 600,
            }}
          >
            VIEWS
          </Typography>
          <ViewChips>
            {completedViews.map((v) => (
              <ViewBadge key={v}>{v}</ViewBadge>
            ))}
          </ViewChips>
        </Box>
      )}
      <Box>
        <Typography
          style={{
            fontSize: rem(11),
            color: EPOCUS_TEXT_SECONDARY,
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          KEY FINDINGS
        </Typography>
        <FindingsText>{keyFindings}</FindingsText>
      </Box>
      {notes && (
        <Box mt={1}>
          <Typography
            style={{
              fontSize: rem(11),
              color: EPOCUS_TEXT_SECONDARY,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            NOTES
          </Typography>
          <FindingsText>{notes}</FindingsText>
        </Box>
      )}
    </Card>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const EpocusReviewPanelPage: FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const user = useUser();

  // Verdict state
  const [agree, setAgree] = useState<boolean | null>(null);
  const [qualityScore, setQualityScore] = useState<number>(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reviewCollection = useRxCollectionEpocusReview();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examCollection = useRxCollectionEpocusExam();

  // Load the exam
  const { result: exams, isFetching: examFetching } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      examId
        ? collection.find({ selector: { id: { $eq: examId } } })
        : undefined,
  );

  const exam = exams[0];

  // Load encounter (for patient name + accession number)
  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) =>
      exam
        ? collection.find({ selector: { id: { $eq: exam.encounterId } } })
        : undefined,
  );

  const encounter = encounters[0];

  // Hydrate images with signed URLs
  const hydratedImages = useEpocusExamImages(exam?.images ?? []);

  const [aiFindingsEnabled] = useFeatureFlag(featureFlags.pocus.geDemo);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  // qualityScore minimum is 1 per global schema
  const canSubmit =
    agree !== null && qualityScore >= 1 && !submitting && !submitted;

  const handleSubmit = async () => {
    if (!canSubmit || !exam || !reviewCollection) return;

    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const reviewDoc = {
        id: randomUniqueId(),
        examId: exam.id,
        reviewerId: user.id,
        agreement: (agree ? "agree" : "disagree") as "agree" | "disagree",
        qualityScore,
        feedbackTags: selectedTags,
        comment: comment || null,
        // Strip RxDB proxy wrapper — inserting a proxied object into another
        // collection causes "property is read-only and non-configurable" errors
        originalFindings: JSON.parse(
          JSON.stringify(exam.worksheetData),
        ) as typeof exam.worksheetData,
        revisedFindings: null,
        meta: {
          createdAt: now,
          createdByUserId: user.id,
          updatedAt: now,
          updatedByUserId: user.id,
        },
      };
      await reviewCollection.insert(reviewDoc);

      // Encounter lifecycle transition (best-effort)
      if (encounterCollection) {
        try {
          const encounterDoc = await encounterCollection
            .findOne({ selector: { id: { $eq: exam.encounterId } } })
            .exec();
          if (encounterDoc) {
            // Check if every exam in this encounter now has a review → "complete"
            let nextStatus: "reviewed" | "complete" = "reviewed";
            if (examCollection) {
              const allExams = await examCollection
                .find({ selector: { encounterId: { $eq: exam.encounterId } } })
                .exec();
              const examIds = allExams.map((e) => e.id);
              if (examIds.length > 0) {
                const existingReviews = await reviewCollection
                  .find({ selector: { examId: { $in: examIds } } })
                  .exec();
                const reviewedExamIds = new Set(
                  existingReviews.map((r) => r.examId),
                );
                if (reviewedExamIds.size >= examIds.length) {
                  nextStatus = "complete";
                }
              }
            }
            await encounterDoc.patch({ status: nextStatus });
          }
        } catch (patchErr) {
          console.warn(
            "[review-panel] encounter patch failed (non-fatal):",
            patchErr,
          );
        }
      }

      setSubmitted(true);
    } catch (err) {
      console.error("[review-panel] insert FAILED:", err);
      if (err && typeof err === "object") {
        console.error("[review-panel] error details:", {
          message: (err as Error).message,
          // RxDB errors expose a `code` field
          code: (err as { code?: string }).code,
          stack: (err as Error).stack,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading state ────────────────────────────────────────────────────────

  if (examFetching || !exam) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress size={32} style={{ color: EPOCUS_PRIMARY }} />
      </Box>
    );
  }

  // ─── Success state ────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <PageRoot>
        <Header>
          <BackButton
            onClick={() => navigate(linkToEpocusReview())}
            aria-label="Back to queue"
          >
            <ArrowBackIcon />
          </BackButton>
          <Box flex={1} minWidth={0}>
            <HeaderTitle>
              {formatDicomPatientName(encounter?.patientName) || "Patient"} —{" "}
              {EXAM_LABELS[exam.examType] ?? exam.examType}
            </HeaderTitle>
          </Box>
        </Header>
        <SuccessBanner>
          <CheckCircleIcon style={{ color: EPOCUS_PRIMARY, fontSize: 56 }} />
          <Typography
            style={{
              fontSize: rem(20),
              fontWeight: 700,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Review Submitted
          </Typography>
          <Typography
            style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}
          >
            Your QA verdict has been recorded.
          </Typography>
          <Button
            variant="outlined"
            style={{
              marginTop: 8,
              borderRadius: 10,
              textTransform: "none",
              borderColor: EPOCUS_PRIMARY,
              color: EPOCUS_PRIMARY,
              minHeight: 44,
            }}
            onClick={() => navigate(linkToEpocusReview())}
          >
            Back to Queue
          </Button>
        </SuccessBanner>
      </PageRoot>
    );
  }

  // ─── Main panel ───────────────────────────────────────────────────────────

  return (
    <PageRoot>
      {/* Header */}
      <Header>
        <BackButton
          onClick={() => navigate(linkToEpocusReview())}
          aria-label="Back to review queue"
        >
          <ArrowBackIcon />
        </BackButton>
        <Box flex={1} minWidth={0}>
          <HeaderTitle>
            {formatDicomPatientName(encounter?.patientName) || "Patient"} —{" "}
            {EXAM_LABELS[exam.examType] ?? exam.examType}
          </HeaderTitle>
          {encounter?.accessionNumber && (
            <HeaderSubtitle>{encounter.accessionNumber}</HeaderSubtitle>
          )}
        </Box>
      </Header>

      <ContentLayout>
        {/* ── Left: Images + Worksheet ── */}
        <LeftColumn>
          {/* Image gallery */}
          <Card style={{ padding: 12 }}>
            <SectionLabel style={{ marginBottom: 8 }}>Images</SectionLabel>
            <EpocusImageGallery
              images={hydratedImages}
              selectedIndex={galleryIndex}
              onImageClick={setGalleryIndex}
            />
          </Card>

          {/* Worksheet summary */}
          <WorksheetCard exam={exam} />

          {aiFindingsEnabled && encounter?.accessionNumber && (
            <AiFindingsPanel accessionNumber={encounter.accessionNumber} />
          )}
        </LeftColumn>

        {/* ── Right: Verdict ── */}
        <RightColumn>
          {/* Agree / Disagree */}
          <Card>
            <SectionLabel>Agree with findings?</SectionLabel>
            <Box display="flex" style={{ gap: 12 }}>
              <VerdictButton
                variant="outlined"
                onClick={() => setAgree(true)}
                aria-pressed={agree === true}
                style={{
                  border: `2px solid ${agree === true ? EPOCUS_PRIMARY : EPOCUS_BORDER}`,
                  backgroundColor:
                    agree === true ? EPOCUS_PRIMARY : EPOCUS_WHITE,
                  color: agree === true ? EPOCUS_WHITE : EPOCUS_TEXT_PRIMARY,
                }}
              >
                <IDocumentsAccepted
                  width={18}
                  height={18}
                  fill={agree === true ? EPOCUS_WHITE : EPOCUS_ICON_ACTIVE}
                  aria-hidden="true"
                  style={{ marginRight: 6 }}
                />
                Agree
              </VerdictButton>
              <VerdictButton
                variant="outlined"
                onClick={() => setAgree(false)}
                aria-pressed={agree === false}
                style={{
                  border: `2px solid ${agree === false ? EPOCUS_PRIMARY : EPOCUS_BORDER}`,
                  backgroundColor:
                    agree === false ? EPOCUS_PRIMARY : EPOCUS_WHITE,
                  color: agree === false ? EPOCUS_WHITE : EPOCUS_TEXT_PRIMARY,
                }}
              >
                <IDocumentsDenied
                  width={18}
                  height={18}
                  fill={agree === false ? EPOCUS_WHITE : EPOCUS_ICON_ACTIVE}
                  aria-hidden="true"
                  style={{ marginRight: 6 }}
                />
                Disagree
              </VerdictButton>
            </Box>
          </Card>

          {/* Star rating */}
          <Card>
            <SectionLabel>Image quality</SectionLabel>
            <EpocusStarRating
              value={qualityScore}
              onChange={(next) => setQualityScore(Math.max(1, next))}
            />
            {qualityScore >= 1 && (
              <Typography
                style={{
                  fontSize: rem(12),
                  color: EPOCUS_TEXT_SECONDARY,
                  marginTop: 6,
                }}
              >
                {qualityScore.toFixed(1)} / 5.0
              </Typography>
            )}
          </Card>

          {/* Feedback chips */}
          <Card>
            <SectionLabel>Feedback tags (optional)</SectionLabel>
            <FeedbackChipRow>
              {FEEDBACK_CHIPS.map((tag) => {
                const selected = selectedTags.includes(tag);
                return (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => toggleTag(tag)}
                    size="small"
                    style={{
                      height: 34,
                      fontSize: rem(13),
                      fontWeight: selected ? 700 : 400,
                      backgroundColor: selected ? EPOCUS_PRIMARY : EPOCUS_WHITE,
                      color: selected ? EPOCUS_WHITE : EPOCUS_TEXT_PRIMARY,
                      border: `1px solid ${selected ? EPOCUS_PRIMARY : EPOCUS_BORDER}`,
                      cursor: "pointer",
                    }}
                  />
                );
              })}
            </FeedbackChipRow>
          </Card>

          {/* Comment */}
          <Card>
            <SectionLabel>Comment (optional)</SectionLabel>
            <TextField
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              variant="outlined"
              placeholder="Add any additional feedback…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              InputProps={{
                style: { fontSize: rem(14), borderRadius: 8 },
              }}
            />
          </Card>

          {/* Submit — gated again inside the route guard so a code path that
              reaches this panel without passing the boundary still can't offer
              an action the API would 403 (PNE-505). */}
          <RoleGate can="submitReview">
            <SubmitButton
              variant="contained"
              disableElevation
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? (
                <CircularProgress size={22} style={{ color: EPOCUS_WHITE }} />
              ) : (
                "Submit Review"
              )}
            </SubmitButton>
          </RoleGate>
        </RightColumn>
      </ContentLayout>
    </PageRoot>
  );
};
